import styled from "styled-components";

const INPUT_DROPDOWN_BG = 'rgba(255, 255, 255, 0.1)';

/* Matches InputDropdown's fieldset so the two are interchangeable when closed */
export const InputDropdownSearchableWrapper = styled.fieldset`
    position: relative;
    box-sizing: border-box;
    flex-shrink: 0;
    width: max-content;
    max-width: 100%;
    min-width: ${props => props.$size === 'compact' ? '80px' : '140px'};
    margin: 0;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    background: ${INPUT_DROPDOWN_BG};
    /* Legend position follows the fieldset's text-align, including inherited center. */
    text-align: ${props => props.$align === 'center' ? 'center' : 'left'};
    transition: border-color 0.2s ease-out, box-shadow 0.2s ease-out;

    &:focus-within {
        border-color: rgba(255, 255, 255, 0.45);
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.12);
    }
`;

export const InputDropdownSearchableControl = styled.div`
    position: relative;
    box-sizing: border-box;
    height: ${props => props.$size === 'compact' ? '30px' : '38px'};
    display: flex;
    align-items: center;
`;

/* In-flow legend — creates a real gap in the fieldset border. No float. */
export const InputDropdownSearchableLabel = styled.legend`
    display: block;
    width: max-content;
    max-width: 100%;
    padding: 0 5px;
    margin-top: 0;
    margin-bottom: 0;
    margin-left: ${props => props.$align === 'center' ? 'auto' : (props.$size === 'compact' ? '4px' : '8px')};
    margin-right: ${props => props.$align === 'center' ? 'auto' : '0'};
    line-height: 1;
    white-space: nowrap;
    color: rgba(255, 255, 255, 0.65);
    font-family: 'Inter', 'Roboto Condensed', sans-serif;
    font-size: ${props => props.$size === 'compact' ? '10px' : '11px'};
    font-weight: 500;
    background: transparent;
`;

export const InputDropdownSearchableTrigger = styled.div`
    box-sizing: border-box;
    color: #FFFFFF;
    cursor: pointer;
    display: block;
    font-family: 'Inter', 'Roboto Condensed', sans-serif;
    font-size: ${props => props.$size === 'compact' ? '12px' : '14px'};
    font-weight: ${props => props.$size === 'compact' ? '500' : '600'};
    height: 100%;
    line-height: ${props => props.$size === 'compact' ? '30px' : '38px'};
    opacity: ${props => props.$isPlaceholder ? '0.5' : '1'};
    outline: none;
    overflow: hidden;
    padding: ${props => props.$size === 'compact'
        ? '0 28px 0 8px'
        : '0 36px 0 12px'};
    text-align: ${props => props.$align === 'center' ? 'center' : 'left'};
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
`;

/* Replaces the trigger while open, so opening never changes the control's height */
export const InputDropdownSearchableSearchField = styled.input`
    appearance: none;
    background: transparent;
    border: none;
    box-sizing: border-box;
    color: #FFFFFF;
    display: block;
    font-family: 'Inter', 'Roboto Condensed', sans-serif;
    font-size: ${props => props.$size === 'compact' ? '12px' : '14px'};
    font-weight: ${props => props.$size === 'compact' ? '500' : '600'};
    height: 100%;
    line-height: ${props => props.$size === 'compact' ? '30px' : '38px'};
    margin: 0;
    min-width: 0;
    outline: none;
    padding: ${props => props.$size === 'compact'
        ? '0 28px 0 8px'
        : '0 36px 0 12px'};
    text-align: ${props => props.$align === 'center' ? 'center' : 'left'};
    width: 100%;

    &::placeholder {
        color: rgba(255, 255, 255, 0.5);
    }
`;

export const InputDropdownSearchableIcon = styled.div`
    position: absolute;
    right: ${props => props.$size === 'compact' ? '6px' : '10px'};
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;

    svg {
        width: ${props => props.$size === 'compact' ? '16px' : '20px'};
        height: ${props => props.$size === 'compact' ? '16px' : '20px'};
    }
`;

/* $portal renders fixed at a measured rect, to escape ancestors that clip overflow.
   Portaled instances get top/left/min-width from inline styles. */
export const InputDropdownSearchablePanel = styled.div`
    backdrop-filter: blur(12px);
    background: rgba(15, 15, 20, 0.96);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    left: ${props => props.$portal ? 'auto' : '0'};
    max-height: 240px;
    max-width: 360px;
    min-width: ${props => props.$portal ? '0' : '100%'};
    overflow-y: auto;
    padding: 4px 0;
    position: ${props => props.$portal ? 'fixed' : 'absolute'};
    top: ${props => props.$portal ? 'auto' : 'calc(100% + 6px)'};
    width: max-content;
    z-index: 200;
`;

export const InputDropdownSearchableOption = styled.div`
    align-items: center;
    background: ${props => props.$isActive ? 'rgba(255, 255, 255, 0.12)' : 'transparent'};
    color: #FFFFFF;
    cursor: pointer;
    display: flex;
    font-family: 'Inter', 'Roboto Condensed', sans-serif;
    font-size: 13px;
    font-weight: ${props => props.$isSelected ? '600' : '500'};
    opacity: ${props => props.$isSelected || props.$isActive ? '1' : '0.75'};
    overflow: hidden;
    padding: 8px 12px;
    text-overflow: ellipsis;
    transition: background 0.15s ease, opacity 0.15s ease;
    white-space: nowrap;
`;

export const InputDropdownSearchableEmpty = styled.div`
    color: rgba(255, 255, 255, 0.5);
    font-family: 'Inter', 'Roboto Condensed', sans-serif;
    font-size: 12px;
    font-weight: 500;
    padding: 10px 12px;
    white-space: nowrap;
`;
